import React from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import MenuItem from '@material-ui/core/MenuItem';

import { updateMergeIntegration } from './actions/update-merge-integration';
import {
  mergeIntegrationForOrgIdSelector,
  mergeIntegrationsStateForOrgIdSelector,
} from './merge-integration-selectors';
import { hrisIntegrationResponseForOrgIdSelector } from '../hris-integration-response/hris-integration-response-selectors';
import { userSelector } from '../reducers/current-user-selectors';

import { UiButton } from '../ui/ui-button';

import { IntegrationEmailType, IntegrationPhoneNumberType } from './util/integration-constants';
import { getHrisDisplayName } from './util/merge-display-utils';
import { capitalize } from '../util/formatters';
import { FormCheckbox } from '../widgets/react-hook-form-components/form-checkbox';
import { FormSelect } from '../widgets/react-hook-form-components/form-select';

type InputProps = {
  org: Org;
  submitCallback: () => void;
};

const mapStateToProps =
  ({ org }: Pick<InputProps, 'org'>) =>
  (state: any) => ({
    mergeIntegration: mergeIntegrationForOrgIdSelector(state, org.org_id),
    mergeIntegrationState: mergeIntegrationsStateForOrgIdSelector(state, org.org_id),
    hrisIntegrationResponse: hrisIntegrationResponseForOrgIdSelector(state, org.org_id),
    user: userSelector(state),
  });

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { org }: Pick<InputProps, 'org'>,
) => ({
  actions: {
    updateMergeIntegration: (update: Partial<MergeIntegration>) =>
      dispatch(updateMergeIntegration(org.org_id, update)),
  },
});

export const MergeIntegrationSettings = ({ org, submitCallback }: InputProps) => {
  const { mergeIntegration, mergeIntegrationState, hrisIntegrationResponse, user } = useSelector(
    mapStateToProps({ org }),
    shallowEqual,
  );

  const { actions } = mapDispatchToProps(useDispatch(), { org });

  const supportsPhoneTypes = mergeIntegration?.HrisIntegrationType?.supportsPhoneNumberType;

  const defaultValues = {
    overwriteExistingPhoneNumber: mergeIntegration?.overwriteExistingPhoneNumber,
    ...(supportsPhoneTypes
      ? { desiredPhoneNumberType: mergeIntegration?.desiredPhoneNumberType }
      : {}),
    desiredEmailType: mergeIntegration?.desiredEmailType,
  };

  const {
    control,
    errors,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm({ mode: 'all', defaultValues });

  if (!user.isAccountAdmin || mergeIntegrationState?.loading || !mergeIntegration) {
    return null;
  }

  const getContactInfoTypeOptionLabel = (
    contactInfoType: IntegrationPhoneNumberType | IntegrationEmailType,
  ) => capitalize(contactInfoType.toLowerCase());

  const onSubmit: SubmitHandler<typeof defaultValues> = ({
    overwriteExistingPhoneNumber: overwriteExistingPhoneNumberValue,
    desiredPhoneNumberType: desiredPhoneNumberTypeValue,
    desiredEmailType: desiredEmailTypeValue,
  }) => {
    if (isDirty) {
      actions.updateMergeIntegration({
        overwriteExistingPhoneNumber: overwriteExistingPhoneNumberValue,
        desiredPhoneNumberType: desiredPhoneNumberTypeValue,
        desiredEmailType: desiredEmailTypeValue,
      });
    }

    submitCallback();
  };

  const hrisDisplayName = getHrisDisplayName(mergeIntegration, hrisIntegrationResponse);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormCheckbox
          control={control}
          name="overwriteExistingPhoneNumber"
          label={`Overwrite Team Engine phone numbers with ${hrisDisplayName || 'HRIS'} numbers`}
        />
        {supportsPhoneTypes && (
          <>
            <div className="u-margin-top typo-subtitle">
              What phone number should we pull in from {hrisDisplayName || 'your HRIS'}?
            </div>
            {control && (
              <FormSelect
                control={control}
                name="desiredPhoneNumberType"
                error={errors.desiredPhoneNumberType}
                required
              >
                {Object.values(IntegrationPhoneNumberType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {getContactInfoTypeOptionLabel(type)}
                  </MenuItem>
                ))}
              </FormSelect>
            )}
          </>
        )}
        <div className="u-margin-top typo-subtitle">
          What email should we pull in from {hrisDisplayName || 'your HRIS'}?
        </div>
        {control && (
          <FormSelect
            control={control}
            name="desiredEmailType"
            error={errors.desiredEmailType}
            required
          >
            {Object.values(IntegrationEmailType).map((type) => (
              <MenuItem key={type} value={type}>
                {getContactInfoTypeOptionLabel(type)}
              </MenuItem>
            ))}
          </FormSelect>
        )}
      </form>
      <div className="u-display-flex u-alignItems-center u-margin-top u-justifyContent-flexEnd">
        <UiButton
          type="submit"
          variant="primary"
          size="sm"
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </UiButton>
      </div>
    </>
  );
};
